/** Accessible shared presentation primitives and user-safe application states. */
import {
  type ButtonHTMLAttributes,
  type FormEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
  useState
} from "react";
import {
  type PasswordRecoveryState,
  getBrowserSession,
  navigateBrowser,
  readSocialAuthCallbackError,
  readSocialAuthProviders,
  requestPasswordRecovery,
  resendEmailConfirmation,
  safeAuthReturnPath,
  signInWithPasswordSession,
  signInWithSocialProvider,
  signUpWithPasswordSession,
  type SocialAuthProvider,
  updateRecoveredPassword
} from "./auth";
import { BrandLogo } from "./brandAssets";
import { Icon } from "./icons";
import { useOptionalInterfaceLanguage } from "./interfaceLanguage";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "small" | "medium" | "large";

export function Button({
  className = "",
  variant = "primary",
  size = "medium",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  return (
    <button
      className={`ui-button ui-button--${variant} ui-button--${size} ${className}`.trim()}
      {...props}
    />
  );
}

export function IconButton({
  label,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      aria-label={label}
      className={`ui-icon-button ${className}`.trim()}
      {...props}
    />
  );
}

export function Card(props: HTMLAttributes<HTMLElement>) {
  return (
    <article {...props} className={`ui-card ${props.className ?? ""}`.trim()} />
  );
}

export function Badge({ children }: { children: ReactNode }) {
  return <span className="ui-badge">{children}</span>;
}

export function Avatar({ name }: { name: string; src?: string }) {
  return (
    <span className="ui-avatar" role="img" aria-label={name}>
      <Icon name="account" />
    </span>
  );
}

export function SearchInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      aria-label="Search Loomua"
      className="ui-search-input"
      type="search"
      {...props}
    />
  );
}

export function Tabs({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="ui-tabs" role="tablist" aria-label={label}>
      {children}
    </div>
  );
}

export function Dialog({
  title,
  children,
  onClose
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <section
      className="ui-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <h2 id="dialog-title">{title}</h2>
      {children}
      <Button onClick={onClose}>Close</Button>
    </section>
  );
}

export function Sheet(props: Parameters<typeof Dialog>[0]) {
  return (
    <div className="ui-sheet">
      <Dialog {...props} />
    </div>
  );
}

export function Toast({ children }: { children: ReactNode }) {
  return (
    <div className="ui-toast" role="status">
      {children}
    </div>
  );
}

export function Skeleton({ label = "Loading" }: { label?: string }) {
  return <span className="ui-skeleton" role="status" aria-label={label} />;
}

export function StatusBanner({
  tone = "info",
  children
}: {
  tone?: "info" | "success" | "warning" | "danger";
  children: ReactNode;
}) {
  return (
    <div className={`ui-status ui-status--${tone}`} role="status">
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  compact = false
}: {
  title?: string;
  message?: string;
  compact?: boolean;
}) {
  const language = useOptionalInterfaceLanguage()?.language ?? "en";
  const zh = language === "zh-CN";
  return (
    <StateLayout
      title={title ?? (zh ? "暂无内容" : "No content")}
      message={message}
      compact={compact}
    />
  );
}

export function ErrorState({
  title,
  message,
  requestId
}: {
  title: string;
  message: string;
  requestId?: string;
}) {
  return (
    <StateLayout title={title} message={message}>
      {requestId ? (
        <p className="ui-request-id">Request ID: {requestId}</p>
      ) : null}
    </StateLayout>
  );
}

type AuthMode = "sign-in" | "register";

const socialAuthProviderLabels: Record<SocialAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  discord: "Discord"
};

const authCopy = {
  en: {
    homeLabel: "Loomua home",
    tagline: "Read and create across languages",
    signInTitle: "Log in to Loomua",
    registerTitle: "Create a Loomua account",
    accountModeLabel: "Account mode",
    signInTab: "Login",
    signInSelected: "Login selected",
    registerTab: "Create account",
    registerSelected: "Create account selected",
    email: "Email",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    forgotPassword: "Forgot password?",
    pleaseWait: "Please wait",
    signedIn: "Signed in. Opening your Loomua workspace.",
    accountCreated: "Account created. Opening your Loomua workspace.",
    authFailed: "Authentication could not be completed.",
    signInFailed: "Email or password could not be verified.",
    registerFailed:
      "Account could not be created. Check the email and password.",
    socialUnavailable: "Social sign-in is unavailable. Try again.",
    socialCallbackFailed:
      "Social sign-in was cancelled or could not be completed. Try again.",
    socialActionsLabel: "Social sign-in",
    socialPrompt: "Or continue with",
    continueWithProvider: (provider: string) => `Continue with ${provider}`,
    checkEmailTitle: "Check your inbox",
    checkEmailMessage:
      "Open the confirmation email to finish creating your Loomua account.",
    sentConfirmation: (email: string) => (
      <>
        Open the confirmation email sent to <strong>{email}</strong>.
      </>
    ),
    sessionMissing:
      "We still cannot find a verified session. Open the email link, then try again.",
    confirmationSent: "Confirmation email sent.",
    confirmationFailed:
      "Confirmation email could not be sent. Try again later.",
    verifiedEmail: "I have verified",
    resendConfirmation: "Resend email",
    useDifferentEmail: "Use a different email",
    signInInstead: "Sign in instead",
    confirmationErrorTitle: "Confirmation link unavailable",
    confirmationErrorMessage:
      "This email confirmation link is invalid or has expired. Request a new link by registering again, or sign in if the account is already confirmed.",
    confirmationErrorAlert: "We could not confirm this email from the link.",
    registerAgain: "Register again",
    signIn: "Sign in",
    confirmationSuccessTitle: "Email confirmed",
    confirmationSuccessMessage:
      "Your Loomua account is ready and this browser is signed in.",
    confirmationSuccessAlert: "Email verification completed successfully.",
    continueToLoomua: "Continue to Loomua",
    recoveryRequestTitle: "Reset your password",
    recoveryRequestMessage:
      "Enter your account email and we will send a secure reset link.",
    recoveryNotice:
      "If an account can be recovered for that email, a reset link is on its way.",
    recoveryFailed: "Password recovery email could not be sent. Try again.",
    sendResetLink: "Send reset link",
    backToSignIn: "Back to sign in",
    recoveryErrorTitle: "Reset link unavailable",
    recoveryErrorMessage: "Request another reset link to continue.",
    recoveryErrorAlert: "This password reset link is invalid or has expired.",
    requestAnotherReset: "Request another reset link",
    newPasswordTitle: "Choose a new password",
    newPasswordMessage: "Use at least 6 characters for your new password.",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    passwordMismatch: "Passwords do not match.",
    passwordUpdated: "Password updated. You can continue to Loomua.",
    passwordUpdateFailed:
      "Password could not be updated. Request a new reset link.",
    updatePassword: "Update password"
  },
  zh: {
    homeLabel: "Loomua 首页",
    tagline: "跨语言阅读与创作",
    signInTitle: "登录 Loomua",
    registerTitle: "创建 Loomua 账号",
    accountModeLabel: "账号模式",
    signInTab: "登录",
    signInSelected: "已选择登录",
    registerTab: "注册",
    registerSelected: "已选择注册",
    email: "邮箱",
    password: "密码",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
    forgotPassword: "忘记密码？",
    pleaseWait: "请稍候",
    signedIn: "已登录，正在打开 Loomua。",
    accountCreated: "账号已创建，正在打开 Loomua。",
    authFailed: "无法完成身份验证。",
    signInFailed: "邮箱或密码无法验证。",
    registerFailed: "无法创建账号，请检查邮箱和密码。",
    socialUnavailable: "第三方登录暂不可用，请稍后重试。",
    socialCallbackFailed: "第三方登录已取消或未完成，请重试。",
    socialActionsLabel: "第三方登录",
    socialPrompt: "或使用以下账号继续",
    continueWithProvider: (provider: string) => `使用 ${provider} 继续`,
    checkEmailTitle: "检查收件箱",
    checkEmailMessage: "打开确认邮件，完成 Loomua 账号创建。",
    sentConfirmation: (email: string) => (
      <>
        打开发送至 <strong>{email}</strong> 的确认邮件。
      </>
    ),
    sessionMissing: "还没有找到已验证会话。请先打开邮件里的链接，再重试。",
    confirmationSent: "确认邮件已重新发送。",
    confirmationFailed: "确认邮件暂时无法发送，请稍后重试。",
    verifiedEmail: "我已确认",
    resendConfirmation: "重新发送",
    useDifferentEmail: "使用其他邮箱",
    signInInstead: "改为登录",
    confirmationErrorTitle: "确认链接不可用",
    confirmationErrorMessage:
      "这个邮箱确认链接无效或已过期。请重新注册获取新链接，或在账号已确认时直接登录。",
    confirmationErrorAlert: "无法通过该链接确认邮箱。",
    registerAgain: "重新注册",
    signIn: "登录",
    confirmationSuccessTitle: "邮箱已确认",
    confirmationSuccessMessage:
      "你的 Loomua 账号已可使用，并且当前浏览器已登录。",
    confirmationSuccessAlert: "邮箱验证已完成。",
    continueToLoomua: "继续使用 Loomua",
    recoveryRequestTitle: "重设密码",
    recoveryRequestMessage: "输入账号邮箱，我们会发送安全的重设链接。",
    recoveryNotice: "如果该邮箱可恢复账号，重设链接会发送到邮箱中。",
    recoveryFailed: "密码重设邮件暂时无法发送，请重试。",
    sendResetLink: "发送重设链接",
    backToSignIn: "返回登录",
    recoveryErrorTitle: "重设链接不可用",
    recoveryErrorMessage: "请重新申请重设链接后继续。",
    recoveryErrorAlert: "这个密码重设链接无效或已过期。",
    requestAnotherReset: "重新申请重设链接",
    newPasswordTitle: "设置新密码",
    newPasswordMessage: "新密码至少需要 6 个字符。",
    newPassword: "新密码",
    confirmNewPassword: "确认新密码",
    passwordMismatch: "两次输入的密码不一致。",
    passwordUpdated: "密码已更新，可以继续使用 Loomua。",
    passwordUpdateFailed: "密码暂时无法更新，请重新申请重设链接。",
    updatePassword: "更新密码"
  }
} satisfies Record<
  "en" | "zh",
  Record<string, string | ((value: string) => ReactNode)>
>;

function useAuthCopy() {
  const language = useOptionalInterfaceLanguage()?.language ?? "en";
  return {
    copy: language === "zh-CN" ? authCopy.zh : authCopy.en,
    zh: language === "zh-CN"
  };
}

function PasswordInput({
  hideLabel,
  showLabel,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  hideLabel: string;
  showLabel: string;
}) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="auth-password-control">
      <input {...props} type={revealed ? "text" : "password"} />
      <button
        aria-label={revealed ? hideLabel : showLabel}
        aria-pressed={revealed}
        className="auth-password-toggle"
        type="button"
        onClick={() => setRevealed((current) => !current)}
      >
        <Icon name={revealed ? "passwordHide" : "passwordShow"} size={18} />
      </button>
    </div>
  );
}

function SocialProviderLogo({ provider }: { provider: SocialAuthProvider }) {
  if (provider === "google") {
    return (
      <svg aria-hidden="true" className="social-auth-logo" viewBox="0 0 48 48">
        <path
          fill="#FFC107"
          d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12S17.4 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9Z"
        />
        <path
          fill="#FF3D00"
          d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7Z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44Z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2C36.9 39.1 44 34 44 24c0-1.3-.1-2.6-.4-3.9Z"
        />
      </svg>
    );
  }

  if (provider === "apple") {
    return (
      <svg aria-hidden="true" className="social-auth-logo" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M12.2 6.9c-.9 0-2.4-1.1-4-1-2 0-3.9 1.2-5 3-2.1 3.7-.5 9.1 1.5 12.1 1 1.5 2.2 3.1 3.8 3 1.5-.1 2.1-1 3.9-1 1.8 0 2.4 1 4 .9 1.6 0 2.7-1.5 3.7-2.9 1.2-1.7 1.6-3.3 1.7-3.4 0 0-3.2-1.2-3.2-4.9 0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.6-2.3-4.4-2.4-2-.1-3.7 1.2-4.6 1.2Zm3.3-3.1C16.4 2.8 17 1.4 16.8 0c-1.2 0-2.7.8-3.5 1.8-.8.9-1.5 2.3-1.3 3.7 1.3.1 2.7-.7 3.5-1.7Z"
        />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      className="social-auth-logo social-auth-logo-discord"
      viewBox="0 0 24 24"
    >
      <path
        fill="currentColor"
        d="M20.3 4.4A19.8 19.8 0 0 0 16.6 3c-.2.3-.4.7-.5 1a18.3 18.3 0 0 0-4.2 0c-.1-.3-.3-.7-.5-1a19.7 19.7 0 0 0-3.8 1.4C5.3 7.9 4.7 11.4 5 14.8a19.9 19.9 0 0 0 4.6 2.3c.4-.5.7-1 .9-1.6-.5-.2-1-.4-1.5-.7.1-.1.3-.2.4-.3a14.2 14.2 0 0 0 12.2 0c.1.1.3.2.4.3-.5.3-1 .5-1.5.7.3.6.6 1.1 1 1.6a19.9 19.9 0 0 0 4.6-2.3c.3-3.9-.7-7.4-2.8-10.4ZM10.6 12.7c-.9 0-1.6-.8-1.6-1.8S9.7 9 10.6 9s1.7.8 1.6 1.9c0 1-.7 1.8-1.6 1.8Zm4.8 0c-.9 0-1.6-.8-1.6-1.8S14.5 9 15.4 9s1.7.8 1.6 1.9c0 1-.7 1.8-1.6 1.8Z"
      />
    </svg>
  );
}

export function AuthWall({
  initialMode = "sign-in",
  returnTo,
  onCheckSession
}: {
  initialMode?: AuthMode;
  returnTo?: string;
  onCheckSession?: () => Promise<boolean>;
}) {
  const { copy, zh } = useAuthCopy();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [pendingEmail, setPendingEmail] = useState<string>();
  const emailId = useId();
  const passwordId = useId();
  const callbackError = readSocialAuthCallbackError(
    window.location.search,
    window.location.hash
  )
    ? copy.socialCallbackFailed
    : undefined;
  const [error, setError] = useState<string | undefined>(callbackError);
  const socialProviders = readSocialAuthProviders(
    import.meta.env as { VITE_SOCIAL_AUTH_PROVIDERS?: string }
  );
  const currentPath = window.location.pathname;
  const fallbackReturnTo =
    currentPath === "/login" || currentPath === "/register"
      ? "/"
      : `${currentPath}${window.location.search}${window.location.hash}`;
  const target = safeAuthReturnPath(
    returnTo ??
      new URLSearchParams(window.location.search).get("returnTo") ??
      (callbackError ? "/" : fallbackReturnTo)
  );

  async function startSocialSignIn(provider: SocialAuthProvider) {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await signInWithSocialProvider(provider, target);
    } catch {
      setError(copy.socialUnavailable);
      setBusy(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      if (mode === "sign-in") {
        await signInWithPasswordSession({ email, password });
        setNotice(copy.signedIn);
        navigateBrowser(target);
        return;
      }
      const result = await signUpWithPasswordSession({ email, password });
      if (result.kind === "signed-in") {
        setNotice(copy.accountCreated);
        navigateBrowser(target);
        return;
      }
      setPassword("");
      setPendingEmail(email);
    } catch (caught) {
      setError(
        caught instanceof Error && !zh
          ? caught.message
          : mode === "sign-in"
            ? copy.signInFailed
            : copy.registerFailed
      );
    } finally {
      setBusy(false);
    }
  }

  async function checkConfirmation() {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    const foundSession = onCheckSession
      ? await onCheckSession()
      : await getBrowserSession().then((session) => {
          if (!session) return false;
          navigateBrowser(target);
          return true;
        });
    if (!foundSession) {
      setNotice(copy.sessionMissing);
      setBusy(false);
    }
  }

  async function resendConfirmation() {
    if (!pendingEmail || busy) return;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await resendEmailConfirmation(pendingEmail);
      setNotice(copy.confirmationSent);
    } catch {
      setError(copy.confirmationFailed);
    } finally {
      setBusy(false);
    }
  }

  if (pendingEmail) {
    return (
      <section
        className="ui-state auth-email-state"
        aria-labelledby="ui-state-title"
      >
        <button
          aria-label={copy.signInInstead}
          className="auth-back-action"
          type="button"
          onClick={() => {
            setPendingEmail(undefined);
            setMode("sign-in");
            setNotice(undefined);
            setError(undefined);
          }}
        >
          <span aria-hidden="true">&lt;</span>
          {copy.signInInstead}
        </button>
        <div className="auth-waiting">
          <h1 id="ui-state-title">{copy.checkEmailTitle}</h1>
          <p className="auth-waiting-summary">
            {copy.sentConfirmation(pendingEmail)}
          </p>
          {error ? (
            <p className="auth-message is-error" role="alert">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p className="auth-message" role="status">
              {notice}
            </p>
          ) : null}
          <div className="auth-actions auth-waiting-actions">
            <Button
              disabled={busy}
              type="button"
              onClick={() => void checkConfirmation()}
            >
              {copy.verifiedEmail}
            </Button>
            <Button
              disabled={busy}
              type="button"
              variant="secondary"
              onClick={() => void resendConfirmation()}
            >
              {copy.resendConfirmation}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const title = mode === "sign-in" ? copy.signInTitle : copy.registerTitle;

  return (
    <section className="auth-page" aria-label={title}>
      <div className="auth-card">
        <a className="auth-brand" href="/" aria-label={copy.homeLabel}>
          <BrandLogo />
        </a>
        <p className="auth-tagline">{copy.tagline}</p>
        <div className="auth-mode-actions" aria-label={copy.accountModeLabel}>
          <button
            aria-label={
              mode === "sign-in" ? copy.signInSelected : copy.signInTab
            }
            aria-pressed={mode === "sign-in"}
            className={`auth-mode-button ${
              mode === "sign-in" ? "is-active" : ""
            }`.trim()}
            type="button"
            onClick={() => {
              setMode("sign-in");
              setNotice(undefined);
              setError(undefined);
            }}
          >
            {copy.signInTab}
          </button>
          <button
            aria-label={
              mode === "register" ? copy.registerSelected : copy.registerTab
            }
            aria-pressed={mode === "register"}
            className={`auth-mode-button ${
              mode === "register" ? "is-active" : ""
            }`.trim()}
            type="button"
            onClick={() => {
              setMode("register");
              setNotice(undefined);
              setError(undefined);
            }}
          >
            {copy.registerTab}
          </button>
        </div>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <div className="auth-field">
            <label htmlFor={emailId}>{copy.email}</label>
            <input
              id={emailId}
              autoComplete="email"
              inputMode="email"
              required
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <div className="auth-label-row">
              <label htmlFor={passwordId}>{copy.password}</label>
              {mode === "sign-in" ? (
                <a className="auth-recovery-link" href="/forgot-password">
                  {copy.forgotPassword}
                </a>
              ) : null}
            </div>
            <PasswordInput
              id={passwordId}
              autoComplete={
                mode === "sign-in" ? "current-password" : "new-password"
              }
              hideLabel={copy.hidePassword}
              minLength={6}
              required
              showLabel={copy.showPassword}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {error ? (
            <p className="auth-message is-error" role="alert">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p className="auth-message" role="status">
              {notice}
            </p>
          ) : null}
          <Button className="auth-submit" type="submit" disabled={busy}>
            {busy
              ? copy.pleaseWait
              : mode === "sign-in"
                ? copy.signInTab
                : copy.registerTab}
          </Button>
        </form>
        {socialProviders.length > 0 ? (
          <div
            className="social-auth-actions"
            aria-label={copy.socialActionsLabel}
          >
            <p>{copy.socialPrompt}</p>
            <div className="social-auth-icon-list">
              {socialProviders.map((provider) => (
                <span className="social-auth-provider" key={provider}>
                  <button
                    aria-label={copy.continueWithProvider(
                      socialAuthProviderLabels[provider]
                    )}
                    className={`social-auth-button is-${provider}`}
                    disabled={busy}
                    type="button"
                    onClick={() => void startSocialSignIn(provider)}
                  >
                    <SocialProviderLogo provider={provider} />
                  </button>
                  <span aria-hidden="true">
                    {socialAuthProviderLabels[provider]}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function EmailConfirmationResult({
  state
}: {
  state: "success" | "error";
}) {
  const { copy } = useAuthCopy();
  if (state === "error") {
    return (
      <StateLayout
        title={copy.confirmationErrorTitle}
        message={copy.confirmationErrorMessage}
      >
        <p className="auth-message is-error" role="alert">
          {copy.confirmationErrorAlert}
        </p>
        <div className="auth-actions">
          <a className="text-button" href="/register">
            {copy.registerAgain}
          </a>
          <a className="text-button" href="/login">
            {copy.signIn}
          </a>
        </div>
      </StateLayout>
    );
  }
  return (
    <StateLayout
      title={copy.confirmationSuccessTitle}
      message={copy.confirmationSuccessMessage}
    >
      <p className="auth-message" role="status">
        {copy.confirmationSuccessAlert}
      </p>
      <a className="text-button" href="/">
        {copy.continueToLoomua}
      </a>
    </StateLayout>
  );
}

export function PasswordRecoveryRequest() {
  const { copy } = useAuthCopy();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice(undefined);
    setError(undefined);
    try {
      await requestPasswordRecovery(email);
      setNotice(copy.recoveryNotice);
    } catch {
      setError(copy.recoveryFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <StateLayout
      title={copy.recoveryRequestTitle}
      message={copy.recoveryRequestMessage}
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label>
          {copy.email}
          <input
            autoComplete="email"
            inputMode="email"
            required
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        {error ? (
          <p className="auth-message is-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="auth-message" role="status">
            {notice}
          </p>
        ) : null}
        <div className="auth-actions">
          <Button disabled={busy} type="submit">
            {busy ? copy.pleaseWait : copy.sendResetLink}
          </Button>
          <a className="text-button" href="/login">
            {copy.backToSignIn}
          </a>
        </div>
      </form>
    </StateLayout>
  );
}

export function RecoveredPasswordForm({
  recoveryState
}: {
  recoveryState: PasswordRecoveryState;
}) {
  const { copy } = useAuthCopy();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();

  if (recoveryState === "error") {
    return (
      <StateLayout
        title={copy.recoveryErrorTitle}
        message={copy.recoveryErrorMessage}
      >
        <p className="auth-message is-error" role="alert">
          {copy.recoveryErrorAlert}
        </p>
        <a className="text-button" href="/forgot-password">
          {copy.requestAnotherReset}
        </a>
      </StateLayout>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(undefined);
    setNotice(undefined);
    if (password !== confirmation) {
      setError(copy.passwordMismatch);
      return;
    }
    setBusy(true);
    try {
      await updateRecoveredPassword(password);
      setNotice(copy.passwordUpdated);
      setPassword("");
      setConfirmation("");
    } catch {
      setError(copy.passwordUpdateFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <StateLayout
      title={copy.newPasswordTitle}
      message={copy.newPasswordMessage}
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label>
          {copy.newPassword}
          <PasswordInput
            autoComplete="new-password"
            hideLabel={copy.hidePassword}
            minLength={6}
            required
            showLabel={copy.showPassword}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <label>
          {copy.confirmNewPassword}
          <PasswordInput
            autoComplete="new-password"
            hideLabel={copy.hidePassword}
            minLength={6}
            required
            showLabel={copy.showPassword}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </label>
        {error ? (
          <p className="auth-message is-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="auth-message" role="status">
            {notice}
          </p>
        ) : null}
        <div className="auth-actions">
          <Button disabled={busy} type="submit">
            {busy ? copy.pleaseWait : copy.updatePassword}
          </Button>
          {notice ? (
            <a className="text-button" href="/">
              {copy.continueToLoomua}
            </a>
          ) : null}
        </div>
      </form>
    </StateLayout>
  );
}

export type SharedStateKind =
  | "loading"
  | "empty"
  | "unauthorized"
  | "forbidden"
  | "not-found"
  | "removed"
  | "hidden"
  | "translation-pending"
  | "translation-failed"
  | "translation-outdated"
  | "network-failure";

const sharedStateCopy: Record<SharedStateKind, [string, string]> = {
  loading: ["Loading", ""],
  empty: ["No content", ""],
  unauthorized: ["Sign in", ""],
  forbidden: ["Unavailable", ""],
  "not-found": ["Not found", ""],
  removed: ["Removed", ""],
  hidden: ["Hidden", ""],
  "translation-pending": ["Translation pending", ""],
  "translation-failed": ["Translation unavailable", ""],
  "translation-outdated": ["Translation updating", ""],
  "network-failure": ["Connection problem", ""]
};

export function SharedState({
  state,
  requestId,
  compact = false
}: {
  state: SharedStateKind;
  requestId?: string;
  compact?: boolean;
}) {
  const language = useOptionalInterfaceLanguage()?.language ?? "en";
  const zh = language === "zh-CN";
  const [englishTitle, englishMessage] = sharedStateCopy[state];
  const chineseCopy: Record<SharedStateKind, [string, string]> = {
    loading: ["正在载入", ""],
    empty: ["暂无内容", ""],
    unauthorized: ["请先登录", ""],
    forbidden: ["无法查看", ""],
    "not-found": ["未找到", ""],
    removed: ["内容已移除", ""],
    hidden: ["内容已隐藏", ""],
    "translation-pending": ["译文生成中", ""],
    "translation-failed": ["暂无译文", ""],
    "translation-outdated": ["译文更新中", ""],
    "network-failure": ["连接出现问题", ""]
  };
  const [title, message] = zh
    ? chineseCopy[state]
    : [englishTitle, englishMessage];

  if (state === "loading") {
    return (
      <section
        className={`ui-loading-state ${compact ? "is-compact" : ""}`}
        role="status"
        aria-live="polite"
      >
        <span className="ui-loading-dot" aria-hidden="true" />
        <span>{title}</span>
      </section>
    );
  }

  return (
    <StateLayout title={title} message={message} compact={compact}>
      {requestId ? (
        <p className="ui-request-id">Request ID: {requestId}</p>
      ) : null}
    </StateLayout>
  );
}

function StateLayout({
  className = "",
  title,
  message,
  children,
  compact = false
}: {
  className?: string;
  title: string;
  message?: string;
  children?: ReactNode;
  compact?: boolean;
}) {
  return (
    <section
      className={`ui-state ${compact ? "is-compact" : ""} ${className}`.trim()}
      aria-labelledby="ui-state-title"
    >
      <h1 id="ui-state-title">{title}</h1>
      {message ? <p>{message}</p> : null}
      {children}
    </section>
  );
}
